/* ============================================================================
   NativeOAuthButtons - shared Google + Apple buttons for /login and /signup.
   ----------------------------------------------------------------------------
   This is the same wiring used inside AuthModal, hoisted into a reusable
   component so the routed Login.tsx and Signup.tsx pages stay in sync with
   the modal automatically. Uses @capgo/capacitor-social-login under the hood
   via @/lib/socialAuth, so iOS / Android use the native Google Sign-In SDK
   and Apple AuthenticationServices, and web falls back to Google Identity
   Services + Apple JS. There is no demo path.
   ========================================================================= */
import { useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { loginWithGoogle, loginWithApple, isAppleSignInAvailable } from "@/lib/socialAuth";
import { authErrorMessage } from "@/lib/apiError";

function GoogleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function AppleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701z" />
    </svg>
  );
}

export interface NativeOAuthButtonsProps {
  /** Called after a successful sign-in once the session token is stored. */
  onSuccess: () => void;
}

export function NativeOAuthButtons({ onSuccess }: NativeOAuthButtonsProps) {
  const { signInWithGoogle, signInWithApple } = useAuth();
  const [oauthBusy, setOauthBusy] = useState<"google" | "apple" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    setError(null);
    setOauthBusy("google");
    try {
      const res = await loginWithGoogle();
      await signInWithGoogle({
        idToken: res.idToken,
        email: res.email ?? "",
        displayName: res.displayName ?? undefined,
        subject: res.subject ?? undefined,
      });
      onSuccess();
    } catch (e: unknown) {
      const msg = authErrorMessage(e, "Google sign-in failed");
      setError(msg);
    } finally {
      setOauthBusy(null);
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setOauthBusy("apple");
    try {
      const res = await loginWithApple();
      await signInWithApple({
        idToken: res.idToken,
        email: res.email ?? "",
        displayName: res.displayName ?? undefined,
        subject: res.subject ?? undefined,
      });
      onSuccess();
    } catch (e: unknown) {
      const msg = authErrorMessage(e, "Apple sign-in failed");
      setError(msg);
    } finally {
      setOauthBusy(null);
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={oauthBusy !== null}
        data-testid="button-continue-google"
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
        style={{
          background: "var(--surface-dark-9)",
          border: "1px solid hsl(142 80% 35% / 0.55)",
          color: "hsl(142 70% 96%)",
        }}
      >
        <GoogleIcon />
        {oauthBusy === "google" ? "Connecting..." : "Continue with Google"}
      </button>
      {/* Hidden on Android — Apple Sign In is not wired there. See
          isAppleSignInAvailable(). */}
      {isAppleSignInAvailable() && (
        <button
          type="button"
          onClick={handleAppleSignIn}
          disabled={oauthBusy !== null}
          data-testid="button-continue-apple"
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
          style={{
            background: "#000",
            border: "1px solid hsl(142 80% 35% / 0.4)",
            color: "#fff",
          }}
        >
          <AppleIcon />
          {oauthBusy === "apple" ? "Connecting..." : "Continue with Apple"}
        </button>
      )}
      {error && (
        <p
          className="text-xs mt-1"
          style={{ color: "hsl(0 80% 70%)" }}
          data-testid="text-oauth-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}
